import {
  ComponentRendering,
  Field,
  FileField,
  GetStaticComponentProps,
  Image,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import classNames from 'classnames';
import { sanitize } from 'isomorphic-dompurify';

import * as icons from '@gn-audio/icons';
import { getContrastThemeClass, JabraIcon, resolveColorProperty } from '@gn-audio/jss-components';

import { JabraGallerySlider, JabraGallerySliderItem } from 'common/components/JabraGalerySlider';
import { getSharedProperty, mapSharedPropertiesToFields } from 'common/getters';
import { getSharedDictionary } from 'common/getters/getSharedDictionary';
import { SitecoreFieldTypes } from 'common/types/sitecore';
import { ComponentProps } from 'lib/component-props';

type GenericKSPSpotLayout = 'vertical' | 'horizontal';

type GenericKSPCardsSpotRendering = {
  fields: {
    cards: GenericKSPCardItem[];
    cardsShared: GenericKSPCardItem[];
    layoutOrientation: Field<GenericKSPSpotLayout>;
    layoutOrientationShared: Field<GenericKSPSpotLayout>;
    alternating: Field<boolean>;
  };
} & ComponentRendering;

type GenericKSPCardItem = {
  cards: GenericKSPCarItemFields[];
};

type GenericKSPCardsSpotFields = {
  cards: GenericKSPCard[];
  layoutOrientation: GenericKSPSpotLayout;
  alternating: boolean;
};

type GenericKSPCarItemFields = {
  image: FileField;
  imageShared: FileField;
  text: Field<string>;
  textShared: Field<string>;
  backgroundColor: Field<string>;
  backgroundColorShared: Field<string>;
  iconDropList: Field<string>;
  iconBackgroundColor: Field<string>;
};

type GenericKSPCardsSpotProps = GenericKSPCardsSpotFields & ComponentProps;
type GenericKSPCard = {
  imgSrc: string;
  imgDisplayName: string;
  text: string;
  backgroundColor: string;
  icon: string;
  iconBackgroundColor: string;
};
type KSPSliderImageProps = {
  item: KSPSliderImage;
  index: number;
  active: boolean;
};
type KSPSliderImage = {
  identifier: string;
  image: string;
  text: string;
  displayName: string;
  backgroundColor: string;
  layoutOrientation: GenericKSPSpotLayout;
  alternating: boolean;
  icon: string;
  iconBackgroundColor: string;
};

const SliderImage = ({ item, index, active }: KSPSliderImageProps) => {
  return (
    <JabraGallerySliderItem
      active={active}
      index={index}
      className={classNames(
        'flex flex-shrink-0 basis-9/12 flex-col items-center overflow-hidden p-4',
        getClassNames(item),
        getContrastThemeClass(item.backgroundColor)
      )}
    >
      {item.layoutOrientation === 'vertical' && item.image ? (
        <VerticalImageLayout item={item} index={index} />
      ) : item.layoutOrientation === 'vertical' ? (
        <VerticalIconLayout item={item} />
      ) : (
        <HorizontalLayout item={item} />
      )}
    </JabraGallerySliderItem>
  );
};

const getClassNames = (item: KSPSliderImage) => {
  if (item.layoutOrientation === 'horizontal') {
    return 'md:basis-1/2 lg:basis-1/2 2xl:basis-1/3';
  } else if (item.icon && !item.image) {
    return 'md:basis-1/2 lg:basis-1/3 2xl:basis-1/4';
  } else {
    return 'md:basis-1/3 lg:basis-1/3 2xl:basis-1/4';
  }
};

const VerticalImageLayout = ({ item, index }: { item: KSPSliderImage; index: number }) => {
  return (
    <div
      className={classNames(
        'flex h-full min-h-[310px] w-full min-w-[100px] flex-col rounded-3xl',
        getContrastThemeClass(item.backgroundColor)
      )}
      style={{ backgroundColor: item.backgroundColor }}
    >
      {item.alternating && index % 2 !== 0 ? (
        <>
          <div className="flex-grow p-6 text-center text-base font-normal leading-6">
            <span dangerouslySetInnerHTML={{ __html: sanitize(item.text) }}></span>
          </div>
          <Image
            className="h-[200px] w-full rounded-b-3xl object-cover"
            field={{
              src: item.identifier,
              alt: item.displayName,
            }}
          />
        </>
      ) : (
        <>
          <Image
            className="h-[200px] w-full rounded-t-3xl object-cover"
            field={{
              src: item.identifier,
              alt: item.displayName,
            }}
          />
          <div className="flex flex-grow p-6 text-center text-base font-normal leading-6">
            <span dangerouslySetInnerHTML={{ __html: sanitize(item.text) }}></span>
          </div>
        </>
      )}
    </div>
  );
};

const VerticalIconLayout = ({ item }: { item: KSPSliderImage }) => (
  <div className="flex h-full min-h-[310px] w-full min-w-[100px] flex-col items-center justify-start">
    <div
      className="flex items-center justify-center rounded-full p-2"
      style={{ backgroundColor: item.backgroundColor }}
    >
      <div
        className="flex items-center justify-center rounded-full p-6"
        style={{ backgroundColor: item.iconBackgroundColor }}
      >
        <JabraIcon
          className={classNames('size-10', getContrastThemeClass(item.iconBackgroundColor))}
          Icon={icons[item.icon as keyof typeof icons]}
        />
      </div>
    </div>

    <div
      className="-z-10 -mt-[42px] size-full rounded-xl p-6 pt-12 text-center text-base font-normal leading-6"
      style={{ backgroundColor: item.backgroundColor }}
    >
      <span dangerouslySetInnerHTML={{ __html: sanitize(item.text) }}></span>
    </div>
  </div>
);

const HorizontalLayout = ({ item }: { item: KSPSliderImage }) => {
  return (
    <div
      className={classNames(
        'flex min-h-28 w-full flex-grow flex-col items-center justify-center rounded-xl sm:flex-row sm:rounded-full sm:text-left',
        getContrastThemeClass(item.backgroundColor)
      )}
      style={{ backgroundColor: item.backgroundColor }}
    >
      {item.image ? (
        <Image
          className="flex h-[220px] w-[220px] rounded-full object-cover p-4 sm:h-[120px] sm:w-[120px] sm:p-0 sm:pr-2"
          field={{
            src: item.identifier,
            alt: item.displayName,
          }}
        />
      ) : (
        <div
          className="mb-6 ml-0 mt-6 flex h-20 w-20 items-center justify-center rounded-full sm:ml-6 md:mb-0 md:mt-0"
          style={{ backgroundColor: item.iconBackgroundColor || 'rgb(0 0 0 / 0.1)' }}
        >
          <JabraIcon
            className={classNames(
              'size-10 sm:mx-6',
              getContrastThemeClass(item.iconBackgroundColor)
            )}
            Icon={icons[item.icon as keyof typeof icons]}
          />
        </div>
      )}

      <div className="flex w-full flex-grow justify-center pb-14 pl-6 pr-8 text-left font-normal leading-6 sm:pb-4 sm:pl-2 sm:pr-14 sm:pt-4 md:text-sm">
        <span dangerouslySetInnerHTML={{ __html: sanitize(item.text) }}></span>
      </div>
    </div>
  );
};

const GenericKSPCardsSpot = ({
  cards,
  layoutOrientation,
  alternating,
  rendering,
}: GenericKSPCardsSpotProps) => {
  const isPartOfGenericKSPSpot = !cards && !rendering.componentName;
  let sliderCards;
  if (isPartOfGenericKSPSpot) {
    const cardsData = prepareGenericKSPCardsProps(
      rendering as GenericKSPCardsSpotRendering
    ) as GenericKSPCardsSpotProps;
    sliderCards = prepareCardsData(
      cardsData.cards,
      cardsData.layoutOrientation,
      cardsData.alternating
    );
  } else {
    sliderCards = prepareCardsData(cards, layoutOrientation, alternating);
  }

  return (
    <div className="max-md:container-fluid md:container">
      <JabraGallerySlider
        itemsAmountStep={1}
        items={sliderCards}
        itemComponent={SliderImage}
        showButtons={true}
        oneItem="max-sm"
        loop={false}
      />
    </div>
  );
};

const prepareCardsData = (
  cards: GenericKSPCard[],
  layoutOrientation: GenericKSPSpotLayout,
  alternating: boolean
) =>
  cards.map((card: GenericKSPCard) => ({
    identifier: card.imgSrc,
    image: card.imgSrc,
    text: card.text,
    displayName: card.imgDisplayName,
    backgroundColor: resolveColorProperty(card.backgroundColor || '#ffd100') || '#ffd100',
    layoutOrientation,
    alternating,
    icon: card.icon,
    iconBackgroundColor: resolveColorProperty(card.iconBackgroundColor || '#A88C70') || '#A88C70',
  }));

const prepareGenericKSPCardsProps = (
  rendering: GenericKSPCardsSpotRendering
): GenericKSPCardsSpotFields | null => {
  if (!rendering.fields) return null;
  const mappedCards = mapSharedPropertiesToFields<GenericKSPCardItem>(
    rendering.fields as SitecoreFieldTypes
  );
  const layoutOrientation =
    getSharedProperty(rendering.fields, 'layoutOrientation')?.value || 'vertical';
  const alternating = getSharedProperty(rendering.fields, 'alternating')?.value || false;
  const cards = mappedCards.cards
    .map((card) => {
      const icon = getSharedProperty(card, 'iconDropList')?.value;
      const iconBackgroundColor = getSharedProperty(card, 'iconBackgroundColor')?.value;
      const img = getSharedProperty(card, 'image')?.value;
      return {
        imgSrc: img?.src,
        imgDisplayName: img?.displayName,
        text: getSharedDictionary(card, 'text')?.value,
        backgroundColor: getSharedProperty(card, 'backgroundColor')?.value,
        icon,
        iconBackgroundColor,
      };
    })
    .filter((card) => card.imgSrc != null || card.icon != null);

  const cardsWithoutUndefined = JSON.parse(
    JSON.stringify(cards, (_, value) => {
      return value === undefined ? null : value;
    })
  );
  return { cards: cardsWithoutUndefined, layoutOrientation, alternating };
};

export const getStaticProps: GetStaticComponentProps = async (
  rendering: GenericKSPCardsSpotRendering
) => {
  return prepareGenericKSPCardsProps(rendering);
};

export default withDatasourceCheck()(GenericKSPCardsSpot);
